import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MapPin, ChevronDown, Facebook, Instagram, Linkedin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('header')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageOpen && !event.target.closest('.language-dropdown')) {
        setIsLanguageOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLanguageOpen]);

  const navigation = [
    { name: t('header.home'), href: '/' },
    { name: t('header.aboutUs'), href: '/about' },
    { name: t('header.specialty'), href: '/specialty' },
    { name: t('header.ourTeam'), href: '/team' },
    { name: t('header.testimonials'), href: '/testimonials' },
    { name: t('header.contactUs'), href: '/contact' },
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const getCurrentLanguage = () => {
    const currentLang = languages.find(lang => lang.code === i18n.language);
    return currentLang || languages[0];
  };

  const handleAddressClick = () => {
    window.open('https://maps.google.com/?q=J43W+JV5,+Kuchaman+-+Sikar+-+Didwana+Rd,+Tilak+Nagar,+Sikar,+Rajasthan+332001', '_blank');
  };

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com/sharmaortho', color: 'hover:text-blue-600' },
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com/sharmaortho', color: 'hover:text-pink-600' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/sharmaortho', color: 'hover:text-blue-700' },
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg' 
        : 'bg-white shadow-sm'
    }`}>
      {/* 🩺 TOP SECTION - Header Info Bar */}
      <div className={`bg-white border-b border-gray-100 transition-all duration-300 ${
        isScrolled ? 'py-3' : 'py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            
            {/* LEFT SIDE - Logo, Hospital Name & Address */}
            <div className="flex items-center space-x-4">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0 group">
                <img
                  src="/assets/onedrive/logo.png"
                  alt="Sharma Ortho Center"
                  className={`rounded-lg bg-white shadow-md transition-all duration-300 group-hover:scale-105 ${
                    isScrolled ? 'h-12 w-12' : 'h-16 w-16'
                  }`}
                />
              </Link>
              
              {/* Hospital Name & Address */}
              <div className="flex flex-col">
                <Link to="/" className="group">
                  <h1 className={`font-bold text-gray-900 leading-tight transition-all duration-300 group-hover:text-primary-600 ${
                    isScrolled ? 'text-xl' : 'text-2xl'
                  }`}>
                    Sharma Ortho Center and Hospital
                  </h1>
                </Link>
                <button
                  onClick={handleAddressClick}
                  className="flex items-center space-x-1 text-gray-600 hover:text-primary-600 transition-all duration-200 group mt-1"
                >
                  <MapPin className="h-4 w-4 group-hover:text-primary-600" />
                  <span className="text-sm font-medium group-hover:underline">
                    Kuchaman - Sikar - Didwana Rd, Tilak Nagar, Sikar, Rajasthan 332001
                  </span>
                </button>
              </div>
            </div>

            {/* RIGHT SIDE - Certifications, Language, Social Media, Book Appointment */}
            <div className="hidden lg:flex items-center space-x-6">
              
              {/* Certification Logos */}
              <div className="flex items-center space-x-3">
                <img
                  src="/assets/onedrive/NABH Logo.jpg"
                  alt="NABH Certified"
                  className="h-8 w-auto opacity-80 hover:opacity-100 transition-opacity duration-200"
                />
                <img
                  src="/assets/onedrive/MAA-Y-removebg-preview.png"
                  alt="ISO Certified"
                  className="h-8 w-auto opacity-80 hover:opacity-100 transition-opacity duration-200"
                />
              </div>

              {/* Vertical Divider */}
              <div className="h-8 w-px bg-gray-300"></div>

              {/* Language Selection */}
              <div className="relative language-dropdown">
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-all duration-200"
                >
                  <span className="text-sm font-medium">{getCurrentLanguage().flag} {getCurrentLanguage().name}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isLanguageOpen ? 'rotate-180' : ''}`} />
                </button>
                {isLanguageOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-20">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`flex items-center space-x-3 w-full text-left px-4 py-2.5 text-sm transition-all duration-200 ${
                          i18n.language === lang.code 
                            ? 'text-primary-600 bg-primary-50 font-semibold' 
                            : 'text-gray-700 hover:bg-primary-50 hover:text-primary-600'
                        }`}
                        onClick={() => changeLanguage(lang.code)}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span className="font-medium">{lang.name}</span>
                        {i18n.language === lang.code && (
                          <span className="ml-auto w-2 h-2 bg-primary-600 rounded-full"></span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Social Media Icons */}
              <div className="flex items-center space-x-3">
                {socialLinks.map((social) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2 rounded-lg text-gray-600 ${social.color} hover:bg-gray-50 transition-all duration-200 hover:scale-110`}
                      aria-label={social.name}
                    >
                      <IconComponent className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>

              {/* Book Appointment Button */}
              <Link
                to="/contact"
                className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 group"
              >
                <span className="relative z-10">{t('header.bookAppointment')}</span>
              </Link>
            </div>

            {/* Mobile Controls */}
            <div className="lg:hidden flex items-center space-x-3">
              <Link
                to="/contact"
                className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
              >
                {t('header.bookAppointment')}
              </Link>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2.5 rounded-xl text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-all duration-300"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🔹 MIDDLE SECTION - Navigation Bar */}
      <nav className={`bg-gray-50/80 backdrop-blur-sm border-b border-gray-200 transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-3'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex justify-center">
            <div className="flex items-center space-x-8">
              {navigation.map((item, index) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 hover:scale-105 ${
                    location.pathname === item.href
                      ? 'text-primary-700 font-semibold'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  {item.name}
                  {/* Active Indicator */}
                  {location.pathname === item.href && (
                    <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-primary-600 animate-pulse" />
                  )}
                  {/* Hover Underline */}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-600 transition-all duration-300 group-hover:w-full" />
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile Navigation Dropdown */}
          <div className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="py-4 space-y-2">
              {navigation.map((item, index) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                    location.pathname === item.href
                      ? 'text-primary-600 bg-primary-50 border-l-4 border-primary-600 font-semibold'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                  style={{ 
                    animationDelay: `${index * 50}ms`,
                    transform: isMenuOpen ? 'translateX(0)' : 'translateX(-20px)',
                    opacity: isMenuOpen ? 1 : 0,
                    transition: `all 0.3s ease-out ${index * 50}ms`
                  }}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Social Links & Language */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center space-x-4">
                    {socialLinks.map((social) => {
                      const IconComponent = social.icon;
                      return (
                        <a
                          key={social.name}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-2 rounded-lg text-gray-600 ${social.color} hover:bg-gray-50 transition-all duration-200`}
                        >
                          <IconComponent className="h-5 w-5" />
                        </a>
                      );
                    })}
                  </div>
                  <button
                    onClick={handleAddressClick}
                    className="flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm">View Location</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;